import type { ReactNode } from "react";
import { Modal } from "./Modal";
import { Button, GHOST_HAIRLINE_ON_HOVER } from "./Button";
import { AlertTriangleIcon } from "./icons";

// ConfirmModal — DS §4.15. confirm / form / destructive. Destructive = danger
// icon-row + tinted footer + danger button; dismiss uses a human verb.

export interface ConfirmModalProps {
    open: boolean;
    onClose: () => void;
    title: string;
    body?: ReactNode;
    confirmLabel: string;
    onConfirm: () => void;
    destructive?: boolean;
    dismissLabel?: string;
    /** Forwarded to {@link Modal}. Pass `false` to keep a stray scrim click from
     *  cancelling a gate that hosts an in-flight real action. Default true. */
    dismissOnScrim?: boolean;
}

export function ConfirmModal({
    open,
    onClose,
    title,
    body,
    confirmLabel,
    onConfirm,
    destructive,
    dismissLabel = "Cancel",
    dismissOnScrim,
}: ConfirmModalProps) {
    const footer = (
        // `whitespace-nowrap` (Button) plus a fixed `lg` padding means the row cannot shrink below
        // its content width — on a phone the danger "Cancel Session" ran past the dialog's right
        // edge (QA-134). Below `sm` the pair stacks full-width instead, confirm on top (DOM order
        // stays Cancel-then-confirm via `flex-col-reverse`, not a JSX reorder, so tab order is
        // untouched) so the destructive action reads apart from Cancel rather than the two being
        // squeezed onto one line. This wrapper is the sole child of Modal's own `justify-between`
        // footer row — one child collapses that to a no-op — so at `sm:` it reproduces the same
        // `justify-between` itself and the row looks exactly as it did before this change; only the
        // phone case QA-117's `wide` bump never reached is different.
        <div className="flex w-full flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            {/* Ghost resolving into a white hairline pill on hover (QA-117) — the same treatment the
                pickers' Back carries, now that this Cancel sits beside a filled `danger` and had no
                visible hover of its own on the tinted footer. */}
            {/* `!` overrides: Button's `lg` is a fixed `h-10` + `whitespace-nowrap`, which is exactly
                the constraint this footer exists to route around. A long label full-width at phone
                width can still be wider than its own box under `nowrap` — the text bled past the
                pill's rounded corners rather than the button growing to hold it. Below `sm` the
                button is let off both leashes: wraps, and grows past `h-10` on a `min-h` floor so a
                two-line label doesn't stay pinned to a one-line height. `sm:` restores both exactly,
                same idiom as GHOST_HAIRLINE_ON_HOVER's own `!` overrides above. */}
            <Button
                variant="ghost"
                size="lg"
                onClick={onClose}
                className={`w-full !h-auto min-h-10 !whitespace-normal !py-2.5 sm:w-auto sm:!h-10 sm:whitespace-nowrap sm:!py-0 ${GHOST_HAIRLINE_ON_HOVER}`}
            >
                {dismissLabel}
            </Button>
            <Button
                variant={destructive ? "danger" : "primary"}
                size="lg"
                className="w-full !h-auto min-h-10 !whitespace-normal !py-2.5 sm:w-auto sm:!h-10 sm:whitespace-nowrap sm:!py-0"
                onClick={() => {
                    onConfirm();
                    onClose();
                }}
            >
                {confirmLabel}
            </Button>
        </div>
    );

    if (destructive) {
        return (
            // `wide` (520px), up from the default 460 (QA-117). Most of the "everything is cramped"
            // was simply the narrowest shell in the set: a question for a title, three lines of
            // consequence under it and two buttons below, in 460px with a × in the corner. 520 lets
            // the title hold one line and the body lose one. Applied to BOTH branches so a confirm
            // does not resize according to whether it happens to be destructive.
            <Modal open={open} onClose={onClose} ariaLabel={title} footer={footer} footerTone="danger" size="wide" dismissOnScrim={dismissOnScrim}>
                <div className="flex items-start gap-4 pb-2">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-danger-bg text-danger">
                        <AlertTriangleIcon size={20} />
                    </span>
                    <div className="min-w-0">
                        {/* `pr-8` RESERVES THE CLOSE BUTTON'S CORNER (QA-117). This branch renders
                            its own heading inside the body — the non-destructive one hands `title` to
                            the Modal, which already keeps that corner clear — so the h2 spanned the
                            full content width and ran UNDER the ×. Measured on the live dialog: the
                            title's right edge sat at 641 against the button's left edge at 623, which
                            is why the "?" appeared tucked behind it.

                            Not `truncate`: this is a question, and a question that has to fit on one
                            line is the wrong question. It wraps, one line short of the corner.

                            26px, not the 38px this first tried: the × overlaps the content column by
                            only 18px, and reserving more than that narrowed EVERY line to protect
                            the first — which pushed this title onto two lines for no gain. */}
                        <h2 className="mb-4 mt-1 pr-8 font-serif text-xl font-medium leading-[1.3] tracking-[-0.005em] text-primary">
                            {title}
                        </h2>
                        {body && (
                            <div className="font-sans text-base leading-[1.5] text-secondary">
                                {body}
                            </div>
                        )}
                    </div>
                </div>
            </Modal>
        );
    }

    return (
        <Modal open={open} onClose={onClose} title={title} footer={footer} size="wide" dismissOnScrim={dismissOnScrim}>
            {body && (
                <div className="font-sans text-base leading-[1.5] text-secondary">
                    {body}
                </div>
            )}
        </Modal>
    );
}
